import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef, ColGroupDef, StatusPanelDef, ExcelRow } from 'ag-grid-community';
import { CalculationsService } from '../../../../../services/calculations/calculations.service';
import { Subscription } from 'rxjs';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { AgNoRowsOverlayComponent } from '../../../../ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { DxsrExcelStyling } from '../dxsr-excel-styling';
import { LoggingService } from '../../../../../services/api/logging.service';
import { AgGridFunctions } from '../../../../../helpers/ag-grid-functions';
import { HccPatientDistributionReportItem } from '../../../../../models/hcc-patient-distribution-report-item';
import { AgLabelStatusPanelComponent, IAgLabelPanel } from '../../../../ag-grid-components/ag-label-status-panel/ag-label-status-panel.component';
import { formatNumber, NgStyle } from '@angular/common';
import { AgLoadingOverlayComponent } from '../../../../ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { AgGridExportHelper, IQueryFilterRow } from '../../../../../helpers/ag-grid-export-helper';

@Component({
    selector: 'app-hcc-patient-distribution-report',
    templateUrl: './hcc-patient-distribution-report.component.html',
    styleUrls: ['./hcc-patient-distribution-report.component.scss'],
    imports: [NgStyle, AgGridModule]
})
export class HccPatientDistributionReportComponent implements OnInit, OnDestroy {

  data: HccPatientDistributionReportItem[] = [];

  columnDefs: (ColDef | ColGroupDef)[] = [];

  defaultColDef: ColDef = {
    menuTabs: []
  };

  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => 'No results returned',
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  statusBar: {
    statusPanels: StatusPanelDef[];
  } = {
    statusPanels: [
      {
        statusPanel: AgLabelStatusPanelComponent,
        key: 'total-panel',
        statusPanelParams: {
          label: 'Total Patients with HCCs'
        }
      },
    ],
  };

  excelStyles = DxsrExcelStyling.excelStyles2;

  gridContext: any;

  rowItems: any[] = [];
  gridReady = false;

  dxSwitch = false;

  currentYear = 0;
  totalPatients = 0;

  currentRowHeight = 140;
  currentGridWidth = 282;

  subs: Subscription[] = [];

  agGridExportHelper = new AgGridExportHelper();

  @Output() ready: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() setExportEnable: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('grid') agGrid!: AgGridAngular;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setGridColumnsToFit();
  }

  constructor(
    private calculationsService: CalculationsService) {
    this.gridContext = { componentParent: this };
  }

  ngOnInit(): void {
    //TODO
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  onGridReady(event) {
    this.ready.emit(true);
  }

  updateServiceYear(serviceYear: number) {
    this.currentYear = serviceYear;
    this.showGridsLoadingOverlay();

    this.subs.push(this.calculationsService.getHccPatientDistribution(serviceYear).subscribe(result => {

      this.data = result.Items;
      this.totalPatients = result.TotalPatients;
      const total = formatNumber(this.totalPatients, 'en');

      this.currentRowHeight = 66 + ((this.data.length + 2) * 29);
      this.currentGridWidth = 600;

      this.agGrid?.api?.setGridOption('rowData', this.data);
      this.agGrid?.api?.setGridOption('columnDefs', this.getColDef());
      this.agGrid?.api?.getStatusPanel<IAgLabelPanel>('total-panel')?.updateLabel(`Total Patients with HCCs: ${total}`);
      this.hideGridsLoadingOverlay();
      this.setExportEnable.emit(true);

      setTimeout(() => {
        this.setGridColumnsToFit();
      }, 500);
    }));
  }

  getColDef(): (ColDef | ColGroupDef)[] {

    const result: any = [
      {
        headerName: `Service Year ${this.currentYear}`,
        children: [
          { field: 'HCCCode', headerName: 'HCC Code', type: 'numericColumn', width: 150, minWidth: 150, maxWidth: 150,
          },
          { field: "PatientCount", headerName: 'PatientCount', type: 'numericColumn', width: 150, minWidth: 150, maxWidth: 150, valueFormatter: AgGridFunctions.getNumberValue,
            cellClass: 'numberType'
          },
          { field: "Progress", headerName: '% of Patient Population with HCC', type: 'numericColumn', flex: 1, minWidth: 300, valueFormatter: (params) => AgGridFunctions.getPercentValue(params, 1),
            cellClass: 'percent'
          }
        ]
      }
    ];

    return result;
  }

  setGridColumnsToFit() {
    if(!document.getElementById('grid')) {
      return;
    }
  }

  showGridsLoadingOverlay() {
    this.agGrid?.api?.setGridOption('loading', true);
  }

  hideGridsLoadingOverlay() {
    this.agGrid?.api?.setGridOption('loading', false);
  }

  onExport() {
    const filename =`HCCPrevalenceWithinPopulationExport_${this.currentYear}`;
    const total = formatNumber(this.totalPatients, 'en');
    const summationRow: ExcelRow[] = [
      {
        cells: [
          {
            data: {
              value: 'Total Patients with HCCs',
              type: 'String'
            },
            styleId: 'summation'
          },
          {
            data: {
              value: total,
              type: 'String'
            },
            styleId: 'summation'
          },
          {
            data: {
              value: '',
              type: 'String'
            },
            styleId: 'summation'
          }
        ]
      }
    ];
    this.agGridExportHelper.onExport(
      filename,
      'HCC Prevalence within Population by Service Year',
      this.getQueryFilterRows(),
      this.agGrid,
      [], // excluded columns
      undefined, // sheet name resolver
      () => summationRow // append content resolver
    );
  }

  getQueryFilterRows(): IQueryFilterRow[] {
    const filters: IQueryFilterRow[] = [];
    filters.push({ label: 'Service Year', value: this.currentYear.toString(), type: 'Number' });
    return filters;
  }
}
